import React, { FC, useEffect, useRef, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Trans } from "react-i18next";
import { t } from "i18next";
import { CsobDataSetDataDto } from "@green0meter/api-client";

import DatasetHeader from "./components/DatasetHeader";
import TableQuestionnaire from "./components/TableQuestionnaire";
import FooterSelectDataSet from "./components/FooterSelectDataSet";
import DataOutdatedBanner from "./components/DataOutdatedBanner";

import { Logo } from "@components/common";
import { Grid } from "@components/base";

interface DatasetComparison {
  csobDatasetComparison: CsobDataSetDataDto;
}

const DatasetComparison: FC<DatasetComparison> = ({ csobDatasetComparison }) => {
  const [isUserViewedContent, setIsUserViewedContent] = useState(false);
  const tableRef = useRef<HTMLDivElement>(null);

  // Check if the table is bigger than the viewport and set the state accordingly
  useEffect(() => {
    if (tableRef.current) {
      const target = tableRef.current;

      const contentBiggerThanViewport = window.outerHeight > target.clientHeight;

      if (contentBiggerThanViewport) {
        setIsUserViewedContent(true);
      }
    }
  }, [tableRef]);

  return (
    <Box display="flex" flexDirection="column" flex={1} height="100%">
      <Grid size={12} position="sticky">
        <Typography fontWeight={500} variant="h6">
          <Trans
            i18nKey="csob.datasetComparison.title"
            components={{
              strong: <Typography component="span" fontSize={20} fontWeight={700} />,
            }}
          />
        </Typography>
        <DatasetHeader csobDatasetComparison={csobDatasetComparison} />
        {csobDatasetComparison.waitingImport && <DataOutdatedBanner />}
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            borderBottom: 0,
          }}
        >
          <Table sx={{ tableLayout: "fixed" }}>
            <TableHead>
              <TableRow sx={{ height: 52 }}>
                <TableCell>
                  <Typography fontWeight={500} paddingLeft={1}>
                    {t("csob.datasetComparison.table.header1")}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Logo type="csob" height={28} />
                </TableCell>
                <TableCell align="center">
                  <Logo type="green0meter-with-text" height={28} />
                </TableCell>
              </TableRow>
            </TableHead>
          </Table>
        </TableContainer>
      </Grid>
      <Grid
        size={12}
        paddingBottom={2}
        sx={{
          flex: 1,
          overflowY: "auto",
        }}
        onScroll={(e) => {
          const target = e.target as HTMLElement;

          const isScrolledToBottom =
            target.scrollTop + target.clientHeight >= target.scrollHeight - 1;

          if (isScrolledToBottom) {
            setIsUserViewedContent(true);
          }
        }}
      >
        <Box ref={tableRef}>
          <TableQuestionnaire csobDatasetComparison={csobDatasetComparison} />
        </Box>
      </Grid>
      <FooterSelectDataSet isUserViewedContent={isUserViewedContent} />
    </Box>
  );
};

export default DatasetComparison;
